import { useUpdateEffect } from 'ahooks';
import React from 'react';
import usePortal, { Args } from 'react-cool-portal';
import { TModal } from '@apia/components';
import useRenderDebug from './useRenderDebug';

export type TUseModalConfiguration = Pick<TModal, 'onClose' | 'onExited'> & {
  debugPerformanceName?: string;
  isDefaultOpen?: boolean;
  onOpen?: () => unknown;
  portalArgs?: Omit<Args, 'containerId'>;
  preFetcher?: () => Promise<void>;
};

/**
 * Este hook provee una forma muy sencilla de utilizar nuestro modal. Provee 
 * varias funcionalidades interesantes para poder manipular el modal en 
 * distintas circunstancias:
 * 
 * Objeto parámetro:
 * 
 * - **defaultOpen**, Boolean, si es true el modal aparecerá abierto al montar 
 * y sino aparecerá cerrado.
 * - **onOpen**: Event listener que será llamado en el momento exacto en que la 
 * orden de apertura del modal fue dada.
 * - **onClose**: Event listener que será llamado en el momento exacto en que 
 * el modal es cerrado, ya sea por un click en el overlay o porque la tecla 
 * escape fue presionada.
 * - **onExited**, Event listener que será llamado luego de que la animación de 
 * cierre del modal terminó.
 * - **preFetcher**, este método deberá devolver una promesa y en dicha promesa 
 * deberá realizar cualquier operación que fuera necesaria antes de abrir el 
 * modal, ejemplo: cargar la información que el modal debe mostrar. Si este 
 * método es pasado, será llamado antes de abrir el modal y la vista se pondrá 
 * en modo de carga (la propiedad isLoading devuelta y la propiedad isLoading 
 * del OpenModalContextProvider estarán en true), hasta que la promesa sea 
 * resuelta, momento en que se abrirá el modal e isLoading será puesto en false.
 * - **portalArgs**, objeto de configuración aceptado por el hook usePortal de 
 * **react-cool-portal**.
 * 
 * Objeto devuelto:
 * 
 * - Para un modal en donde el botón de abrir el modal esté inserto dentro del 
 * componente que lo utiliza, se devuelven los métodos **show** y **hide**.
 * - Para un modal en donde el botón de abrir el modal esté fuera del 
 * componente, se devuelve **openModalContextValue**, que es el valor que debe 
 * pasarse al contexto de tipo OpenModalContextProvider, un contexto que 
 * puede ser utilizado para renderizar el botón encargado de abrir el modal, 
 * fuera del alcance del componente que define el modal en sí. Se muestra un 
 * ejemplo más abajo.
 * - Todos los demás elementos devueltos son para pasar directamente al modal, 
 * entre ellos se encuentran las propiedades que el modal utiliza para saber si 
 * debe estar abierto, los event listeners correspondientes y un portal, que el 
 * modal usará para mostrarse dentro del área de modales, lo cual es sumamente 
 * importante para que el focusController funcione correctamente.
 * 
 * @see OpenModalContextProvider
 * @see usePortal
 * 
 * @example
 * 
  const { openModalContextValue, ...modalProps } = useModal(
    React.useMemo(() => ({
      onClose: () => console.log("Al presionar en cerrar."),
      onExited: () => console.log("Al terminar la animación de cierre."),
      onOpen: () => console.log("Al abrir."),
    }), [])
  );

  return (
    <OpenModalContext.Provider value={openModalContextValue}>
      <OpenModalContext.Consumer>
        {({ isLoading, show }) => (
          <Button isLoading={isLoading} onClick={show}>Open modal</Button>
        )}
      </OpenModalContext.Consumer>
      <Modal shouldCloseOnEsc shouldCloseOnOverlayClick {...modalProps}>
        Hello world
      </Modal>
    </OpenModalContext.Provider>
  );
 */
export default function useModal(configuration?: TUseModalConfiguration) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(
    configuration?.portalArgs?.defaultShow ??
      configuration?.isDefaultOpen ??
      false,
  );
  const {
    Portal,
    hide: hidePortal,
    show: showPortal,
  } = usePortal({
    ...configuration?.portalArgs,
    containerId: 'AppModals',
    autoRemoveContainer: false,
    escToHide: false,
    clickOutsideToHide: false,
    defaultShow: false,
    internalShowHide: false,
  });

  const hide = React.useCallback(() => {
    setIsOpen(false);
  }, []);
  const show = React.useCallback(() => {
    if (configuration?.preFetcher) {
      setIsLoading(true);
      configuration
        .preFetcher()
        .then(() => {
          setIsOpen(true);
          setIsLoading(false);
        })
        .catch(console.error);
    } else setIsOpen(true);
  }, [configuration]);
  const onClose = React.useCallback(() => {
    setIsOpen(false);
    if (configuration?.onClose) configuration.onClose();
  }, [configuration]);
  const onExited = React.useCallback(() => {
    hidePortal();
    if (configuration?.onExited) configuration.onExited();
    setIsOpen(false);
  }, [configuration, hidePortal]);
  const openModalContextValue = React.useMemo(
    () => ({
      isLoading,
      show,
    }),
    [isLoading, show],
  );

  useUpdateEffect(() => {
    if (isOpen) {
      if (configuration?.onOpen) configuration.onOpen();
      showPortal();
    } else onClose?.();
  }, [isOpen]);

  useRenderDebug(false, configuration?.debugPerformanceName ?? '', {
    ...configuration,
    isLoading,
    isOpen,
    Portal,
    hidePortal,
    showPortal,
    openModalContextValue,
  });

  return {
    hide,
    isLoading,
    isOpen,
    onClose,
    onExited,
    openModalContextValue,
    Portal,
    show,
  };
}
